import { BaseDialog } from '@/components/BaseDialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useConfirm } from '@/hooks/useConfirm'
import { useImageUrl } from '@/hooks/useFile'
import { useUI } from '@/hooks/useUI'
import { isApiError } from '@/utils/error'
import { showError, showSuccess } from '@/utils/toast'
import { Copy, Eye, EyeOff, Loader2, RotateCcwKey, User as UserIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetUserById, useResetUserPassword } from '../hooks/useUser'

function UserDetailAvatar({
  avatarId,
  name,
  username,
  alt,
}: {
  avatarId?: string | null
  name?: string | null
  username?: string | null
  alt: string
}) {
  const { data: avatarUrl } = useImageUrl(avatarId || undefined)
  const fallback = (name || username || 'U').slice(0, 2).toUpperCase()

  return (
    <Avatar className="size-12 rounded-md border border-border/40">
      {avatarUrl ? <AvatarImage src={avatarUrl} alt={name || username || alt} className="object-cover" /> : null}
      <AvatarFallback className="rounded-md text-xs font-medium text-muted-foreground">{fallback}</AvatarFallback>
    </Avatar>
  )
}

export function UserDetailDialog({
  open,
  onOpenChange,
  userId,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  userId?: string | null
}) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const { isAdmin } = useAuth()
  const { userRoles, getEnumLabel } = useUI()
  const { data: user, isLoading } = useGetUserById(userId)
  const resetPassword = useResetUserPassword()
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (open) {
      setGeneratedPassword(null)
      setShowPassword(false)
    }
  }, [open, userId])

  async function handleResetPassword() {
    if (!userId || generatedPassword) return

    const confirmed = await confirm({
      variant: 'destructive',
      title: t('users.resetPasswordConfirmTitle'),
      description: t('users.resetPasswordConfirmDescription', {
        name: user?.name || user?.username || t('users.user'),
      }),
    })

    if (!confirmed) return

    try {
      const password = await resetPassword.mutateAsync(userId)
      setGeneratedPassword(password)
      setShowPassword(false)
      showSuccess(t('users.resetPasswordSuccess'))
    } catch (error) {
      if (isApiError(error)) {
        showError(error.message)
        return
      }
      showError(t('users.resetPasswordError'))
    }
  }

  async function handleCopyPassword() {
    if (!generatedPassword) return

    try {
      await navigator.clipboard.writeText(generatedPassword)
      showSuccess(t('users.copyPasswordSuccess'))
    } catch {
      showError(t('users.copyPasswordError'))
    }
  }

  if (!userId) return null

  return (
    <BaseDialog
      open={open}
      onOpenChange={onOpenChange}
      header={
        <>
          <UserIcon className="size-5" />
          <span>{t('users.userDetails')}</span>
        </>
      }
    >
      {isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-26 w-full" />
        </div>
      ) : user ? (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3 rounded-lg border border-border/30 bg-secondary/30 p-4">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-muted-foreground">{t('common.avatar')}</span>
              <UserDetailAvatar
                avatarId={user.avatarId}
                name={user.name}
                username={user.username}
                alt={t('users.userDetails')}
              />
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">{t('common.username')}</span>
              <span className="text-sm font-medium text-foreground">{user.username}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">{t('common.name')}</span>
              <span className="text-sm font-medium text-foreground">{user.name}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">{t('common.role')}</span>
              <span className="text-sm font-medium text-foreground">{getEnumLabel(userRoles, user.role)}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">{t('common.primaryDepartment')}</span>
              <span className="text-sm font-medium text-foreground">{user.primaryDepartment?.name || '-'}</span>
            </div>
            <div className="col-span-2 flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">{t('users.responsibleDepartments')}</span>
              <span className="text-sm font-medium text-foreground">
                {user.departments.length > 0 ? user.departments.map(d => d.name).join(', ') : '-'}
              </span>
            </div>
          </div>
          {isAdmin && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                    <RotateCcwKey className="size-4" />
                    <span>{t('users.resetPassword')}</span>
                  </div>
                  <div className="whitespace-pre-line text-xs leading-relaxed text-destructive/80">
                    {t('users.resetPasswordDescription')}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="h-8 shrink-0 text-xs"
                  onClick={handleResetPassword}
                  disabled={resetPassword.isPending || !!generatedPassword}
                >
                  {resetPassword.isPending ? (
                    <Loader2 className="mr-1 size-3 animate-spin" />
                  ) : (
                    <RotateCcwKey className="mr-1 size-3" />
                  )}
                  {generatedPassword ? t('users.passwordReset') : t('users.resetPassword')}
                </Button>
              </div>

              {generatedPassword && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-border/30 bg-background px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-muted-foreground">{t('users.generatedPassword')}</div>
                    <div className="truncate font-mono text-sm text-foreground">
                      {showPassword ? generatedPassword : '*'.repeat(generatedPassword.length)}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 shrink-0"
                    onClick={() => setShowPassword(value => !value)}
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    <span className="sr-only">
                      {showPassword ? t('common.hidePassword') : t('common.showPassword')}
                    </span>
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 shrink-0"
                    onClick={handleCopyPassword}
                  >
                    <Copy className="size-4" />
                    <span className="sr-only">{t('users.copyPassword')}</span>
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">{t('users.noUserData')}</div>
      )}
    </BaseDialog>
  )
}
